import axios from 'axios';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RouterProvider } from 'react-router-dom';
import { IntlProvider } from 'react-intl';
import { ThemeProvider } from '@zenqms/zui';

import Header from './components/Header';
import router from './routes';
import { syncWithSession } from './auth/auth-slice';
import Toast from './components/Toast';
import ErrorBoundary from './components/ErrorBoundary';
import IdleSessionNotifier from './components/IdleSessionNotifier';
import Footer from './components/Footer';
import { flattenMessages } from './translations/flattenMessages';
import { messages } from './translations';
import { switchLocale } from './app/app-slice';

let testModeDataLoader = Promise.resolve();
if (window.config && window.config.enableTestMode) {
  testModeDataLoader = import('./modes/test.js');
}

export function App() {
  const dispatch = useDispatch();
  const localeFromState = useSelector((state) => state.app.locale);
  const locale = App.locale(localeFromState);

  useEffect(() => {
    dispatch(syncWithSession());
    dispatch(switchLocale(App.locale(locale)));
  }, [locale]);

  if (App.testMode()) {
    axios.defaults.adapter = (requestConfig) => {
      if (!window.testMode) {
        return testModeDataLoader.then(() => window.testMode.responseFor(requestConfig));
      }
      return window.testMode.responseFor(requestConfig);
    };
  }

  return (
    <>
      <ErrorBoundary>
        <IntlProvider locale={locale} messages={flattenMessages(messages[locale])} defaultLocale="en-US">
          <ThemeProvider>
            <>
              <Toast />
              <Header />
              <RouterProvider router={router} />
              <IdleSessionNotifier />
              <Footer />
            </>
          </ThemeProvider>
        </IntlProvider>
      </ErrorBoundary>
    </>
  );
}

App.testMode = () => {
  const appMode = new URLSearchParams(window.location.search).get('mode');
  const enableTestMode = window.config.enableTestMode;
  return enableTestMode && appMode === 'test';
};

App.locale = (localeFromState) => {
  let locale = new URLSearchParams(window.location.search).get('lang');
  if (!locale) {
    locale = localeFromState;
  }
  if (!locale) {
    locale = 'en-US';
  }
  return locale;
};
